import axios, { AxiosError, AxiosResponse } from 'axios';
import moment from 'moment';
import React, { createRef, LegacyRef } from 'react';
import NotificationSystem, { Style, System } from 'react-notification-system';
import {
  clearAccessToken,
  getAccessToken,
  getLanguage,
  setAccessToken,
  setLanguage
} from 'services/browserStorageServices';
import { getCurrentUser, retrieveAccessToken } from 'services/userServices';
import { Privilege } from 'ts-types/api.enums';
import { Module, TenantConfigCore, UserConfigDto, UserDto } from 'ts-types/api.types';
import { ModuleDef, MODULES, MODULES_MAP } from 'utils/moduleUtils';

type LoginFunction = (username: string, password: string, verificationCode?: string) => Promise<any>;

const NotificationSystemStyle: Style = {
  NotificationItem: {
    DefaultStyle: {
      margin: '60px 5px 5px 5px'
    }
  }
};

export interface Options {
  workDate: string,
  currentTreater: {
    id: number,
    name: string
  }
}

interface State {
  isAuthenticated: boolean,
  currentUser?: UserDto,
  getMandate: () => number,
  login?: LoginFunction,
  logout?: () => void,
  options: Options,
  setOptions: (options: Options) => void,
  currentUserHasPrivilege(privilege: Privilege): boolean,
  moduleEnabledForUser(module: Module): boolean,
  moduleAccessible(module: Module): boolean,
  initialModule: () => ModuleDef | undefined,
  updateUserConfig: (userConfigDto: UserConfigDto) => void,
  notificationSystem?: LegacyRef<System>,
  fetchingUser: boolean,
  fetchingMessages: boolean,
  setFetchingMessages: (fetchingMessages: boolean) => void,
  sessionExpiredModalOpen: boolean,
  onSessionExpiredModalClose: () => void,
  onSessionExpiredModalOpen: () => void,
  language: string,
  updateLanguage: (lang: string) => void,
  config?: TenantConfigCore
}

const AuthContext = React.createContext({
  isAuthenticated: false,
  getMandate: (): number => 1,
  options: {
    workDate: moment().format("YYYY-MM-DD"),
    currentTreater: {
      id: -1,
      name: ""
    }
  },
  setOptions: (options: Options) => {},
  currentUserHasPrivilege: (privilege: Privilege): boolean => false,
  moduleEnabledForUser: (module: Module): boolean => false,
  moduleAccessible: (module: Module): boolean => false,
  initialModule: (): ModuleDef | undefined => MODULES[0],
  updateUserConfig: (userConfigDto: UserConfigDto) => {},
  fetchingUser: true,
  fetchingMessages: true,
  setFetchingMessages: (fetchingMessages: boolean) => {},
  sessionExpiredModalOpen: false,
  onSessionExpiredModalClose: () => {},
  onSessionExpiredModalOpen: () => {},
  language: getLanguage(),
  updateLanguage: (lang: string) => {}
});

interface Props extends JSX.ElementChildrenAttribute {
}

export class AuthProvider extends React.Component<Props, State> {

  constructor(props: any) {
    super(props);
    this.state = {
      isAuthenticated: !!getAccessToken(),
      getMandate: this.getMandate,
      login: this.login,
      logout: this.logout,
      options: {
        workDate: moment().format("YYYY-MM-DD"),
        currentTreater: {
          id: -1,
          name: ""
        }
      },
      setOptions: this.setOptions,
      currentUserHasPrivilege: this.currentUserHasPrivilege,
      moduleEnabledForUser: this.moduleEnabledForUser,
      moduleAccessible: this.moduleAccessible,
      initialModule: this.initialModule,
      updateUserConfig: this.updateUserConfig,
      notificationSystem: createRef<System>(),
      fetchingUser: !!getAccessToken(),
      fetchingMessages: true,
      setFetchingMessages: this.setFetchingMessages,
      sessionExpiredModalOpen: false,
      onSessionExpiredModalClose: this.closeSessionExpiredModal,
      onSessionExpiredModalOpen: this.showSessionExpiredModal,
      language: getLanguage(),
      updateLanguage: this.updateLanguage
    };

    axios.interceptors.response.use(response => response, this.sessionExpiredHandler);

    if (getAccessToken()) {
      this.fetchCurrentUser();
    }
  }

  getMandate = () => {
    return this.state.currentUser!.userConfig.mandateId;
  };

  sessionExpiredHandler = (error: AxiosError) => {

    const {response} = error;
    const {sessionExpiredModalOpen} = this.state;

    if (response && response.status === 401) {
      if (!sessionExpiredModalOpen && !this.isPathExcludedFromSessionModal(response.data.path)) {
        this.showSessionExpiredModal();
      }
    }

    return Promise.reject(error);
  };

  isPathExcludedFromSessionModal = (path: string): boolean => {

    const excludedPaths = ["auth", "password-reset", "public"];
    let excluded = false;

    for (const excludedPath of excludedPaths) {
      if (path.includes(excludedPath)) {
        excluded = true;
        break;
      }
    }

    return excluded;
  };

  login: LoginFunction = (username, password, verificationCode) => {
    return retrieveAccessToken(username, password, verificationCode)
        .then((response: AxiosResponse) => {

          const accessToken = response.headers['authorization'].replace('Bearer ', '');
          setAccessToken(accessToken);

          const optionsReset = {
            workDate: moment().format("YYYY-MM-DD")
          };

          return this.fetchCurrentUser(optionsReset);
        });
  };

  fetchCurrentUser = (optionsReset: Partial<Options> = {}) => {

    const {options} = this.state;

    return getCurrentUser()
        .then(user => {

          const language = user.userConfig.language ? user.userConfig.language : getLanguage();

          this.setState({
            isAuthenticated: user.userConfig.mandateId != null,
            sessionExpiredModalOpen: false,
            currentUser: user,
            options: {
              ...options,
              ...optionsReset,
              currentTreater: {
                id: user.userId,
                name: user.treaterName
              }
            },
            language: language,
            config: user.tenantConfig
          });

          this.updateLanguage(language);

          return user;
        })
        .catch(this.logout)
        .finally(() => {
          this.setState({
            fetchingUser: false
          });
        });
  };

  logout = () => {
    clearAccessToken();
    return this.setState({isAuthenticated: false, currentUser: undefined, sessionExpiredModalOpen: false});
  };

  setOptions = (options: Options) => {
    this.setState({
      options: {...options}
    })
  };

  currentUserHasPrivilege = (privilege: Privilege): boolean => {
    const {currentUser} = this.state;
    return currentUser !== undefined
        && currentUser.userConfig
        && currentUser.userConfig.privileges.includes(privilege);
  }

  moduleEnabledForUser = (module: Module): boolean => {
    const {currentUser} = this.state;
    return currentUser !== undefined
        && currentUser.userConfig
        && currentUser.userConfig.modules.includes(module);
  }

  moduleAccessible = (module: Module): boolean => {

    const requiredPrivilege = MODULES_MAP[module].privilege;
    const hasRequiredPrivilege = !requiredPrivilege || this.currentUserHasPrivilege(requiredPrivilege);

    return hasRequiredPrivilege && this.moduleEnabledForUser(module);
  }

  initialModule = (): ModuleDef | undefined => {
    return MODULES.find(module => module.enabled && this.moduleAccessible(module.code));
  }

  updateUserConfig = (userConfigDto: UserConfigDto) => {
    const {currentUser} = this.state;

    let updatedCurrentUser: any = {
      ...currentUser
    };
    updatedCurrentUser!.userConfig = userConfigDto;

    this.setState({
      currentUser: updatedCurrentUser,
      isAuthenticated: true
    })
  };

  updateLanguage = (lang: string) => {
    this.setState({
      language: lang
    });
    setLanguage(lang);
    moment.locale(lang);
  };

  setFetchingMessages = (fetchingMessages: boolean) => {
    this.setState({
      fetchingMessages: fetchingMessages
    })
  };

  showSessionExpiredModal = () => {
    this.setState(({sessionExpiredModalOpen}: State) => ({
      sessionExpiredModalOpen: true
    }))
  };

  closeSessionExpiredModal = () => {
    this.setState(({sessionExpiredModalOpen}: State) => ({
      sessionExpiredModalOpen: false
    }))
  };

  render() {
    return (
        <AuthContext.Provider value={this.state}>
          {this.props.children}
          <NotificationSystem ref={this.state.notificationSystem} style={NotificationSystemStyle}/>
        </AuthContext.Provider>
    )
  }
}

export const AuthConsumer = AuthContext.Consumer;

export interface AuthConsumerRenderProps extends State {
}

export const withAuthContext = (Component: typeof React.Component | React.FunctionComponent<any>) => {
  return (props: any) => (
      <AuthConsumer>
        {({...authProps}: AuthConsumerRenderProps) => {
          return <Component {...props} {...authProps} />
        }}
      </AuthConsumer>
  );
};
